// src/api/content.ts
import { API_BASE, buildHeaders, fetchWithAuth } from "../utils/api";
import { get as storeGet, put as storePut, compositeKey } from "../utils/store";

export type DayContent = {
  title: string | null;
  dashboard_text: string | null;
  main_text: string | null;
  extra_tasks: string | null;
  special_instructions: string | null;
  congrats_text: string | null;
  xp: number | null;
};

export type DayMedia = {
  audio_url: string | null;
  video_url: string | null;
};

// M5-STEP3A-FIX (16.09.2026): status по образцу SnapshotResult (snapshot.ts) —
// те же три имени, не вторая схема. ok сохранён как было (форму читает
// refetchAll в DashboardScreen) — status добавлен рядом, не вместо.
// "empty" — 404/400/403/401: день_не_заведён/day_out_of_range/day_locked/
// уже обработанный 401 — законное "текста для этого дня нет", не отказ.
// "unavailable" — сеть, неожиданный !res.ok (включая 5xx), и в кэше тоже пусто.
// M5-STORE2 (16.09.2026): добавлен "cached" — та же логика, что в snapshot.ts:
// отказ спросить — не сразу unavailable, сперва IndexedDB (content-access,
// инвариант 9). cachedAt/stale едут вместе с данными, как у снапшота.
export type ContentDayResponse = {
  ok: boolean;
  status: "ok" | "empty" | "unavailable" | "cached";
  day: DayContent | null;
  media: DayMedia;
  cachedAt?: number;
  stale?: boolean;
};

const EMPTY_MEDIA: DayMedia = { audio_url: null, video_url: null };

const EMPTY_CONTENT: ContentDayResponse = {
  ok: false,
  status: "empty",
  day: null,
  media: EMPTY_MEDIA,
};

const UNAVAILABLE_CONTENT: ContentDayResponse = {
  ok: false,
  status: "unavailable",
  day: null,
  media: EMPTY_MEDIA,
};

type StoredContent = { day: DayContent; media: DayMedia };

/** Спросить кэш при сетевом отказе. Пусто в кэше → unavailable, как раньше. */
async function cachedOrUnavailable(key: string): Promise<ContentDayResponse> {
  const cached = await storeGet<StoredContent>(key);
  if (cached.status === "empty") return UNAVAILABLE_CONTENT;
  console.error(`[content] served from cache, age=${Date.now() - cached.cachedAt}`);
  return {
    ok: true,
    status: "cached",
    day: cached.data.day,
    media: cached.data.media,
    cachedAt: cached.cachedAt,
    stale: cached.status === "stale",
  };
}

export async function getContentDay(
  courseId: number | string,
  day: number | string
): Promise<ContentDayResponse> {
  const key = compositeKey("content_day", courseId, day);

  let res: Response;
  try {
    const qs = new URLSearchParams({
      course_id: String(courseId),
      day: String(day),
    });
    res = await fetchWithAuth(`${API_BASE}/api/content/day?${qs.toString()}`, {
      headers: buildHeaders(false),
    });
  } catch (e) {
    console.error("[content] network error:", e instanceof Error ? e.message : String(e));
    return cachedOrUnavailable(key);
  }

  // Законные "текста для этого дня нет" — не логируются, штатные исходы.
  // Легитимно пусто — в кэш не кладём и в кэш не заглядываем (могла быть
  // предыдущая запись под тем же ключом за другой день_locked/статус курса).
  if (res.status === 404 || res.status === 400 || res.status === 403 || res.status === 401) {
    return EMPTY_CONTENT;
  }

  if (!res.ok) {
    console.error("[content] unexpected error status:", res.status);
    return cachedOrUnavailable(key);
  }

  try {
    const data = (await res.json()) as ContentDayResponse;
    const result: ContentDayResponse = { ...data, ok: true, status: "ok" };
    if (result.day) {
      // userId: нет локального источника личности в этом модуле (в отличие от
      // snapshot.ts, которому user_id приходит в самом ответе) — не нужен: смена
      // пользователя на устройстве чистит store целиком через clearAll(),
      // отдельная проверка здесь была бы дублированием той же гарантии.
      await storePut(key, { day: result.day, media: result.media }, 0);
    }
    return result;
  } catch (e) {
    console.error("[content] 200 but json parse failed:", e instanceof Error ? e.message : String(e));
    return cachedOrUnavailable(key);
  }
}

/** Склеивает относительный путь медиа (/api/media/<uuid>?exp&sig) с API_BASE */
export function mediaUrl(rel: string | null): string | null {
  if (!rel) return null;
  return `${API_BASE}${rel}`;
}
